import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  createTheme,
  Dialog,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  ThemeProvider,
  Zoom,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import {
  CloudUploadRounded,
  DateRange,
  Delete,
  DeleteOutlineRounded,
  Save,
} from "@mui/icons-material";
import { CustomImageView } from "../../../component/CustomImageView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import {
  STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_PRODUCT,
  STRINGS_CREATE,
  STRINGS_DELETE,
  STRINGS_DELETE_PRODUCT,
  STRINGS_PRODUCT,
  STRINGS_SAVE,
} from "../../../../data/strings/Strings";
import { useEffect, useRef, useState } from "react";
import { CustomTextField } from "../../../component/CustomTextField";
import { CustomTextView } from "../../../component/CustomTextView";
import { HeaderView } from "../../../component/HeaderView";
import { ROUTE_NEW } from "../../../../data/constant/RouteConstants";
import { CategoryDetailsViewModel } from "./ProductDetailsViewModel";
import { FilePicker } from "../../../component/FilePicker";
import {
  provideCreateProductUseCase,
  provideDeleteProductUseCase,
  provideGetAllCategoriesUseCase,
  provideGetAllStoresUseCase,
  provideGetProductUseCase,
  provideUpdateProductUseCase,
  provideUploadMultipleFilesUseCase,
} from "../../../di/UseCasesModule";
import { CustomAlertDialog } from "../../../component/CustomAlertDialog";
import { formatDate, hasEnglishLetters } from "../../../../core/utils/Utils";
import Marquee from "react-fast-marquee";
import { LoadingView } from "../../../component/LoadingView";
import { MAIN_FONT_FAMILY } from "../../../../data/constant/Constants";
import { CustomButtonView } from "../../../component/CustomButtonView";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import {
  LocalizationProvider,
  StaticDateTimePicker,
} from "@mui/x-date-pickers";
import { NumericFormat } from "react-number-format";
import { CustomCardView } from "../../../component/CustomCardView";
import Masonry from "@mui/lab/Masonry";

export function ProductDetailsView() {
  const { id } = useParams();
  const viewModel = CategoryDetailsViewModel(
    provideGetProductUseCase(),
    provideCreateProductUseCase(),
    provideDeleteProductUseCase(),
    provideUpdateProductUseCase(),
    provideUploadMultipleFilesUseCase(),
    provideGetAllCategoriesUseCase(),
    provideGetAllStoresUseCase()
  );
  const { width } = useWindowDimensions();
  const isNewPage = id === ROUTE_NEW;
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);

  useEffect(() => {
    if (!isNewPage && id && viewModel.categories?.length != 0) {
      viewModel.fetchProduct(id);
    }
  }, [viewModel.categories]);

  useEffect(() => {
    if (viewModel.categories.length > 0 && viewModel.stores.length > 0) {
      if (isNewPage) {
        viewModel.setLoading(false);
      } else if (viewModel.product._id) {
        viewModel.setLoading(false);
      }
    }
  }, [viewModel.categories, viewModel.stores, viewModel.product]);

  return (
    <Box>
      <CustomAlertDialog
        title={STRINGS_DELETE_PRODUCT}
        description={STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_PRODUCT}
        open={showDeleteAlertDialog}
        handleNegativeButtonClick={() => setShowDeleteAlertDialog(false)}
        handlePositiveButtonClick={() => {
          viewModel.deleteProduct();
          setShowDeleteAlertDialog(false);
        }}
      />
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={viewModel.openDialog}
        onClose={() => {
          if (!viewModel.product?.goingLiveAt) return;
          viewModel.setOpenDialog(false);
        }}
        PaperProps={{
          sx: {
            borderRadius: 6,
            p: 2,
            minHeight: "30vh",
            background: COLOR_WHITE,
          },
        }}
      >
        <Stack spacing={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DateTimePicker"]}>
              <StaticDateTimePicker
                disablePast
                value={
                  viewModel.product?.goingLiveAt
                    ? dayjs(viewModel.product?.goingLiveAt)
                    : undefined
                }
                onChange={(d) => {
                  const date = d?.toDate();
                  date?.setSeconds(0);
                  viewModel.setProduct({
                    ...viewModel.product,
                    goingLiveAt: date,
                  });
                }}
                slotProps={{
                  layout: { sx: { fontFamily: MAIN_FONT_FAMILY } },
                  actionBar: { actions: ["today"] },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
          <Button
            disabled={!viewModel.product?.goingLiveAt}
            onClick={() => {
              viewModel.setOpenDialog(false);
            }}
            variant="outlined"
            sx={{
              borderRadius: 12,
              alignSelf: "end",
              border: !viewModel.product?.goingLiveAt
                ? undefined
                : "1px solid black",
            }}
          >
            <CustomTextView
              text="Set & Close"
              variant="body1"
              props={{ textTransform: "none" }}
              textColor={
                !viewModel.product?.goingLiveAt ? COLOR_LESS_BLACK : COLOR_BLACK
              }
            />
          </Button>
        </Stack>
      </Dialog>
      <Stack>
        {viewModel.isLoading ? (
          <LoadingView />
        ) : (
          <>
            <Stack>
              <HeaderView
                isLayoutFullWidthInSmallerScreens={!isNewPage}
                title={STRINGS_PRODUCT}
                alignItems="start"
                titleLayout={
                  <>
                    {viewModel.product?._id && (
                      <Marquee style={{ width: 100 }} delay={2} pauseOnHover>
                        <CustomTextView
                          text={`#${viewModel.product?._id}`}
                          fontWeight="300"
                          variant="h6"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                      </Marquee>
                    )}
                  </>
                }
                layout={
                  <Stack
                    alignItems={"center"}
                    justifyContent={!isNewPage && width < 900 ? "start" : "end"}
                    spacing={1}
                    width={"100%"}
                  >
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={
                        !isNewPage && width < 900 ? "start" : "end"
                      }
                      spacing={1}
                      width={"100%"}
                    >
                      {!isNewPage && (
                        <Button
                          onClick={() => setShowDeleteAlertDialog(true)}
                          variant="outlined"
                          fullWidth
                          sx={{
                            borderRadius: 22,
                            borderColor: "red",
                            "&:hover": {
                              borderColor: "red",
                            },
                          }}
                        >
                          <Delete fontSize="small" sx={{ color: "red" }} />
                          <CustomTextView
                            text={STRINGS_DELETE}
                            fontWeight="bold"
                            textColor={"red"}
                            fontSize={16}
                            props={{ ml: 1, textTransform: "none" }}
                          />
                        </Button>
                      )}
                      <Button
                        onClick={() => viewModel.saveOrCreateProduct(isNewPage)}
                        variant="outlined"
                        fullWidth={!isNewPage}
                        sx={{
                          borderRadius: 22,
                          borderColor: COLOR_BLACK,
                          "&:hover": {
                            borderColor: COLOR_BLACK,
                          },
                        }}
                      >
                        <Save fontSize="small" sx={{ color: COLOR_BLACK }} />
                        <CustomTextView
                          text={isNewPage ? STRINGS_CREATE : STRINGS_SAVE}
                          fontWeight="bold"
                          fontSize={16}
                          props={{ ml: 1, textTransform: "none" }}
                          textColor={COLOR_BLACK}
                        />
                      </Button>
                    </Stack>
                  </Stack>
                }
              />

              <Masonry
                columns={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2 }}
                spacing={2}
                sx={{ m: 0, pl: 2, pr: 2, pb: 2 }}
              >
                <CustomCardView
                  padding={2}
                  children={
                    <Stack spacing={2}>
                      <CustomTextView
                        text="Name & Description"
                        fontWeight="bold"
                        variant="h5"
                      />
                      <Stack spacing={1}>
                        <CustomTextView
                          text="Name*"
                          textColor={COLOR_LIGHT_GRAY}
                          variant="body2"
                        />
                        <CustomTextField
                          value={viewModel.product?.name}
                          fontSize={16}
                          fontWeight="bold"
                          placeholder={"Edit Name"}
                          onTextChange={(t) =>
                            viewModel.setProduct({
                              ...viewModel.product,
                              name: t,
                            })
                          }
                          sxProps={{
                            direction: "ltr",
                          }}
                        />
                      </Stack>
                      <Stack spacing={1}>
                        <CustomTextView
                          text="Name In Arabic"
                          textColor={COLOR_LIGHT_GRAY}
                          variant="body2"
                        />
                        <CustomTextField
                          value={viewModel.product?.nameInArabic}
                          fontSize={16}
                          fontWeight="bold"
                          placeholder={"Edit Name in Arabic"}
                          onTextChange={(t) =>
                            viewModel.setProduct({
                              ...viewModel.product,
                              nameInArabic: t,
                            })
                          }
                          sxProps={{
                            direction: "rtl",
                          }}
                        />
                      </Stack>
                      <Stack spacing={1}>
                        <CustomTextView
                          text="Description"
                          textColor={COLOR_LIGHT_GRAY}
                          variant="body2"
                        />
                        <CustomTextField
                          value={viewModel.product?.description}
                          fontSize={16}
                          fontWeight="bold"
                          multiLine
                          placeholder={"Edit Description"}
                          onTextChange={(t) =>
                            viewModel.setProduct({
                              ...viewModel.product,
                              description: t,
                            })
                          }
                          sxProps={{
                            direction: "ltr",
                          }}
                        />
                      </Stack>
                      <Stack spacing={1}>
                        <CustomTextView
                          text="Description In Arabic"
                          textColor={COLOR_LIGHT_GRAY}
                          variant="body2"
                        />
                        <CustomTextField
                          value={viewModel.product?.descriptionInArabic}
                          fontSize={16}
                          fontWeight="bold"
                          multiLine
                          placeholder={"Edit Description In Arabic"}
                          onTextChange={(t) =>
                            viewModel.setProduct({
                              ...viewModel.product,
                              descriptionInArabic: t,
                            })
                          }
                          sxProps={{
                            direction: "rtl",
                          }}
                        />
                      </Stack>
                    </Stack>
                  }
                />
                <CustomCardView
                  padding={2}
                  children={
                    <Stack spacing={2}>
                      <CustomTextView
                        text="Details"
                        fontWeight="bold"
                        variant="h5"
                      />
                      <Stack>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                        >
                          <CustomTextView
                            text="Going Live At*"
                            textColor={COLOR_LIGHT_GRAY}
                            variant="body2"
                          />
                          <IconButton
                            onClick={() => {
                              viewModel.setOpenDialog(true);
                            }}
                          >
                            <DateRange sx={{ color: COLOR_LIGHT_GRAY }} />
                          </IconButton>
                        </Stack>
                        <CustomTextView
                          text={
                            viewModel.product?.goingLiveAt
                              ? formatDate(
                                  new Date(viewModel.product?.goingLiveAt)
                                )
                              : "no date chosen yet"
                          }
                          textColor={
                            viewModel.product?.goingLiveAt
                              ? COLOR_BLACK
                              : COLOR_LIGHT_GRAY
                          }
                          fontWeight={
                            viewModel.product?.goingLiveAt ? "bold" : "300"
                          }
                          variant={viewModel.product?.goingLiveAt ? "h5" : "h6"}
                        />
                      </Stack>
                      <Divider sx={{ background: COLOR_LIGHT_GRAY }} />
                      <Stack>
                        <CustomTextView
                          text="Images (min 1 and max 6)"
                          textColor={COLOR_LIGHT_GRAY}
                          variant="body2"
                        />
                        <Stack spacing={1}>
                          <Stack
                            height={200}
                            width={"100%"}
                            alignItems={"center"}
                            justifyContent={"center"}
                          >
                            <FilePicker
                              handleFilePicked={viewModel.addImages}
                              layout={
                                <CloudUploadRounded
                                  sx={{
                                    color: COLOR_MAIN,
                                    width: 140,
                                    height: 140,
                                  }}
                                />
                              }
                              acceptType="image/*"
                            />
                          </Stack>
                          <Grid container spacing={1} pl={1} pr={1} pb={1}>
                            {viewModel.product?.imagesList?.map(
                              (image, index) => {
                                const imageToShow =
                                  image instanceof File
                                    ? URL.createObjectURL(image as File)
                                    : (image as any).downloadUrl;
                                return (
                                  <Grid
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 6,
                                      lg: 6,
                                      xl: 4,
                                    }}
                                    key={index}
                                    sx={{ height: 150 }}
                                  >
                                    <Stack
                                      position={"relative"}
                                      width={"100%"}
                                      height={"100%"}
                                    >
                                      <CustomImageView
                                        width={"100%"}
                                        height={150}
                                        src={imageToShow}
                                        scale="cover"
                                        props={{
                                          borderRadius: 4,
                                          border: `solid ${
                                            viewModel.product?.thumbnailIndex ==
                                            index
                                              ? COLOR_MAIN + " 3px"
                                              : COLOR_LIGHT_GRAY + " 1px"
                                          }`,
                                        }}
                                      />
                                      <Stack
                                        position={"absolute"}
                                        height={"100%"}
                                        justifyContent={"space-between"}
                                        p={0.5}
                                        spacing={0.5}
                                      >
                                        <IconButton
                                          onClick={() => {
                                            viewModel.removeImage(index);
                                          }}
                                          sx={{
                                            width: 30,
                                            height: 30,
                                            background:
                                              "rgba(113, 112, 112, 0.5)",
                                            border:
                                              "1px solid " + COLOR_LIGHT_GRAY,
                                          }}
                                        >
                                          <DeleteOutlineRounded
                                            sx={{ color: COLOR_LIGHT_GRAY }}
                                            fontSize="small"
                                          />
                                        </IconButton>
                                        {viewModel.product?.thumbnailIndex !=
                                          index && (
                                          <CustomButtonView
                                            text="Set as Product Thumbnail"
                                            onClick={() => {
                                              viewModel.setProduct({
                                                ...viewModel.product,
                                                thumbnailIndex: index,
                                              });
                                            }}
                                            backgroundColor={COLOR_WHITE + "33"}
                                            fontSize="caption"
                                            textColor={"lightGray"}
                                          />
                                        )}
                                      </Stack>
                                    </Stack>
                                  </Grid>
                                );
                              }
                            )}
                          </Grid>
                        </Stack>
                      </Stack>
                    </Stack>
                  }
                />
                {!isNewPage && (
                  <CustomCardView
                    padding={2}
                    children={
                      <Stack spacing={2}>
                        <CustomTextView
                          text="Information"
                          fontWeight="bold"
                          variant="h5"
                        />
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          spacing={2}
                        >
                          <Stack spacing={1}>
                            <CustomTextView
                              text="Sold Quantity"
                              textColor={COLOR_LIGHT_GRAY}
                              variant="body2"
                            />
                            <CustomTextView
                              text={viewModel.product?.soldQty?.toString()}
                              fontWeight="bold"
                              variant="h6"
                            />
                          </Stack>

                          <Stack spacing={1} alignItems={"center"}>
                            <CustomTextView
                              text="Reserved Quantity"
                              textColor={COLOR_LIGHT_GRAY}
                              variant="body2"
                              textAlign="center"
                            />
                            <CustomTextView
                              text={viewModel.product?.reservedQty?.toString()}
                              fontWeight="bold"
                              variant="h6"
                            />
                          </Stack>
                          <Stack spacing={1} alignItems={"end"}>
                            <CustomTextView
                              text="Remaining Amount"
                              textColor={COLOR_LIGHT_GRAY}
                              variant="body2"
                              textAlign="end"
                            />
                            <CustomTextView
                              text={`${
                                (viewModel.product?.totalQty ?? 0) -
                                ((viewModel.product?.reservedQty ?? 0) +
                                  (viewModel.product?.soldQty ?? 0))
                              }`}
                              fontWeight="bold"
                              variant="h6"
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    }
                  />
                )}
                <CustomCardView
                  padding={2}
                  children={
                    <Stack spacing={2}>
                      <CustomTextView
                        text="Pricing & Quantity"
                        fontWeight="bold"
                        variant="h5"
                      />

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
                          <Stack spacing={1}>
                            <CustomTextView
                              text="Total Quantity*"
                              textColor={COLOR_LIGHT_GRAY}
                              variant="body2"
                            />
                            <NumericFormat
                              value={viewModel.product?.totalQty}
                              thousandSeparator
                              allowNegative={false}
                              decimalScale={0}
                              fixedDecimalScale
                              placeholder="Total Quantity"
                              customInput={TextField}
                              onValueChange={(qty) => {
                                viewModel.setProduct({
                                  ...viewModel.product,
                                  totalQty: qty.floatValue,
                                });
                              }}
                              {...{
                                InputProps: {
                                  sx: {
                                    borderRadius: 44,
                                    color: COLOR_BLACK,
                                    fontFamily: MAIN_FONT_FAMILY,
                                    fontWeight: "bold",
                                    fontSize: 16,
                                  },
                                },
                                sx: {
                                  color: COLOR_BLACK,
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: COLOR_LIGHT_GRAY,
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: COLOR_BLACK,
                                      },
                                    "&.Mui-focused fieldset": {
                                      borderColor: COLOR_BLACK,
                                    },
                                  },
                                },
                              }}
                            />
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
                          <Stack spacing={1}>
                            <CustomTextView
                              text="Starting Price* (QR)"
                              textColor={COLOR_LIGHT_GRAY}
                              variant="body2"
                            />
                            <NumericFormat
                              value={viewModel.product?.actualPrice}
                              thousandSeparator
                              allowNegative={false}
                              suffix={" QR"}
                              placeholder="Actual Price"
                              customInput={TextField}
                              onValueChange={(price) => {
                                viewModel.setProduct({
                                  ...viewModel.product,
                                  actualPrice: price.floatValue,
                                });
                              }}
                              {...{
                                InputProps: {
                                  sx: {
                                    borderRadius: 44,
                                    color: COLOR_BLACK,
                                    fontFamily: MAIN_FONT_FAMILY,
                                    fontWeight: "bold",
                                    fontSize: 16,
                                  },
                                },
                                sx: {
                                  color: COLOR_BLACK,
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: COLOR_LIGHT_GRAY,
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: COLOR_BLACK,
                                      },
                                    "&.Mui-focused fieldset": {
                                      borderColor: COLOR_BLACK,
                                    },
                                  },
                                },
                              }}
                            />
                          </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
                          <Stack spacing={1}>
                            <CustomTextView
                              text="Minimum Price* (QR)"
                              textColor={COLOR_LIGHT_GRAY}
                              variant="body2"
                            />
                            <NumericFormat
                              value={viewModel.product?.minimumPrice}
                              thousandSeparator
                              allowNegative={false}
                              suffix={" QR"}
                              placeholder="Minimum Price"
                              customInput={TextField}
                              onValueChange={(price) => {
                                viewModel.setProduct({
                                  ...viewModel.product,
                                  minimumPrice: price.floatValue,
                                });
                              }}
                              {...{
                                InputProps: {
                                  sx: {
                                    borderRadius: 44,
                                    color: COLOR_BLACK,
                                    fontFamily: MAIN_FONT_FAMILY,
                                    fontWeight: "bold",
                                    fontSize: 16,
                                  },
                                },
                                sx: {
                                  color: COLOR_BLACK,
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: COLOR_LIGHT_GRAY,
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: COLOR_BLACK,
                                      },
                                    "&.Mui-focused fieldset": {
                                      borderColor: COLOR_BLACK,
                                    },
                                  },
                                },
                              }}
                            />
                          </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
                          <Stack spacing={1}>
                            <CustomTextView
                              text="Price Decrease Rate* (-QR/sec)"
                              textColor={COLOR_LIGHT_GRAY}
                              variant="body2"
                            />
                            <NumericFormat
                              value={viewModel.product?.priceDecreaseRate}
                              thousandSeparator
                              allowNegative={false}
                              suffix={" QR"}
                              placeholder="Price Decrease Rate"
                              customInput={TextField}
                              onValueChange={(price) => {
                                viewModel.setProduct({
                                  ...viewModel.product,
                                  priceDecreaseRate: price.floatValue,
                                });
                              }}
                              {...{
                                InputProps: {
                                  sx: {
                                    borderRadius: 44,
                                    color: COLOR_BLACK,
                                    fontFamily: MAIN_FONT_FAMILY,
                                    fontWeight: "bold",
                                    fontSize: 16,
                                  },
                                },
                                sx: {
                                  color: COLOR_BLACK,
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: COLOR_LIGHT_GRAY,
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: COLOR_BLACK,
                                      },
                                    "&.Mui-focused fieldset": {
                                      borderColor: COLOR_BLACK,
                                    },
                                  },
                                },
                              }}
                            />
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  }
                />

                <CustomCardView
                  padding={2}
                  children={
                    <Stack spacing={2}>
                      <CustomTextView
                        text="Category"
                        fontWeight="bold"
                        variant="h5"
                      />
                      {viewModel.categories &&
                        viewModel.categories.length > 0 &&
                        viewModel.selectedCategoryId && (
                          <Select
                            sx={{
                              fontFamily: MAIN_FONT_FAMILY,
                              borderRadius: 12,
                              m: 2,
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: COLOR_LIGHT_GRAY,
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: COLOR_BLACK,
                              },
                              "& .MuiSvgIcon-root": {
                                color: COLOR_BLACK,
                              },
                            }}
                            inputProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    background: COLOR_WHITE,
                                  },
                                },
                              },
                            }}
                            value={
                              viewModel.getCategoryById(
                                viewModel.selectedCategoryId
                              )?.title
                            }
                            onChange={(e: SelectChangeEvent<string>) => {
                              const id = viewModel.getCategoryByTitle(
                                e.target.value
                              )?._id;
                              if (!id) return;
                              viewModel.setSelectedCategoryId(id);
                            }}
                          >
                            {Object.values(viewModel.categories).map(
                              (category, index) => {
                                return (
                                  <MenuItem
                                    key={category._id}
                                    sx={{
                                      fontFamily: MAIN_FONT_FAMILY,
                                      direction: hasEnglishLetters(
                                        category.title ?? ""
                                      )
                                        ? "ltr"
                                        : "rtl",
                                    }}
                                    value={category.title}
                                    divider={
                                      index !=
                                      (viewModel.categories?.length ?? 0) - 1
                                    }
                                  >
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      sx={{
                                        direction: hasEnglishLetters(
                                          category.title ?? ""
                                        )
                                          ? "ltr"
                                          : "rtl",
                                      }}
                                      justifyContent={"space-between"}
                                      width={"100%"}
                                    >
                                      <CustomTextView text={category.title} />
                                      <CustomImageView
                                        width={40}
                                        height={30}
                                        scale="cover"
                                        src={category.imageUrl}
                                      />
                                    </Stack>
                                  </MenuItem>
                                );
                              }
                            )}
                          </Select>
                        )}

                      <CustomTextView
                        text="Store"
                        fontWeight="bold"
                        variant="h5"
                      />
                      {viewModel.stores &&
                        viewModel.stores.length > 0 &&
                        viewModel.selectedStoreId && (
                          <Select
                            sx={{
                              fontFamily: MAIN_FONT_FAMILY,
                              borderRadius: 12,
                              m: 2,
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: COLOR_LIGHT_GRAY,
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: COLOR_BLACK,
                              },
                              "& .MuiSvgIcon-root": {
                                color: COLOR_BLACK,
                              },
                            }}
                            inputProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    background: COLOR_WHITE,
                                  },
                                },
                              },
                            }}
                            value={viewModel.selectedStoreId}
                            onChange={(e: SelectChangeEvent<string>) => {
                              const id = e.target.value;
                              if (!id) return;
                              viewModel.setSelectedStoreId(id);
                            }}
                          >
                            {Object.values(viewModel.stores).map(
                              (store, index) => {
                                return (
                                  <MenuItem
                                    key={store._id}
                                    sx={{
                                      fontFamily: MAIN_FONT_FAMILY,
                                      direction: hasEnglishLetters(
                                        store.name ?? ""
                                      )
                                        ? "ltr"
                                        : "rtl",
                                    }}
                                    value={store._id ?? ""}
                                    divider={
                                      index !=
                                      (viewModel.categories?.length ?? 0) - 1
                                    }
                                  >
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      sx={{
                                        direction: hasEnglishLetters(
                                          store.name ?? ""
                                        )
                                          ? "ltr"
                                          : "rtl",
                                      }}
                                      justifyContent={"space-between"}
                                      width={"100%"}
                                    >
                                      <CustomTextView text={store.name} />
                                      <CustomImageView
                                        width={40}
                                        height={30}
                                        scale="cover"
                                        src={store.logo}
                                      />
                                    </Stack>
                                  </MenuItem>
                                );
                              }
                            )}
                          </Select>
                        )}
                    </Stack>
                  }
                />
              </Masonry>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
